<template>
  <div class="home-feed">
    <FeedComposer
      v-if="isLoggedIn && !searchActive"
      :userName="userName"
      :userAvatar="userAvatar"
      @create-post="onCreatePost"
      @create-poll="onCreatePoll"
    />

    <FeedFilter
      v-if="!searchActive"
      :filters="filters"
      v-model="activeFilter"
    />

    <div v-if="searchActive" class="feed-search-active">
      <span class="feed-search-query">
        <f7-icon f7="magnifyingglass" size="14" />
        {{ searchQuery }}
      </span>
      <f7-link class="feed-search-clear" @click="clearSearch">
        <f7-icon f7="xmark" size="14" />
      </f7-link>
    </div>

    <div
      v-if="isLoggedIn && important.event && !searchActive"
      class="feed-important"
    >
      <ImportantEventCard :event="important.event" @open="openImportantEvent" />
    </div>
    <div v-if="isLoggedIn && !searchActive && groupId" class="feed-manage-bar">
      <f7-link
        class="feed-manage-link"
        icon-f7="gear_alt"
        size="14"
        :text="$t('feed.manageTitle')"
        @click="goManage"
      />
    </div>
    <div v-if="feedItems.length === 0 && !loading" class="feed-empty">
      <f7-icon f7="magnifyingglass" size="28" color="#c0c0c0" />
      <p v-if="searchActive">{{ $t("common.nothingFound") }}</p>
      <p v-else>{{ $t("feed.noContentForFilter") }}</p>
    </div>

    <FeedItemCard
      v-for="item in feedItems.slice(0, visibleCount)"
      :key="item.id"
      :item="item"
      :currentUserId="currentUserId.value"
      :chatGroups="chatGroupList"
      :userName="userName"
      :userAvatar="userAvatar"
      :canManage="canManageItem(item)"
      @open="openItemPopup"
      @react="onReact"
      @comment="onComment"
      @repost="onRepost"
      @vote="onVote"
      @shareToChat="onShareToChat"
      @react-comment="onReactComment"
      @reply="onReply"
      @edit="onEditItem"
      @delete="onDeleteItem"
      @pin="onPinItem"
    />

    <ManageFeedDialogs
      ref="manageDialogs"
      @updated="feedStore.updateItem"
      @deleted="feedStore.removeItem"
    />

    <AdaptiveSheet
      v-model:opened="itemPopupOpened"
      style="max-height: auto;"
      class="feed-item-popup"
      :close-on-escape="true"
      swipe-to-close
      :tablet-fullscreen="false"
      :backdrop="false"
      :close-by-backdrop-click="true"
      @closed="selectedItem = null"
    >
      <f7-page>
        <f7-navbar>
          <f7-nav-right>
            <f7-link
              icon-f7="xmark"
              @click="itemPopupOpened = false"
            />
          </f7-nav-right>
        </f7-navbar>
        <f7-block>
          <div v-if="selectedItem" class="feed-item-popup-inner">
            <FeedItemCard
              :item="selectedItem"
              :currentUserId="currentUserId.value"
              :chatGroups="chatGroupList"
              :userName="userName"
              :userAvatar="userAvatar"
              :canManage="canManageItem(selectedItem)"
              :open-on-click="false"
              @react="onReact"
              @comment="onComment"
              @repost="onRepost"
              @vote="onVote"
              @shareToChat="onShareToChat"
              @react-comment="onReactComment"
              @reply="onReply"
              @edit="onEditItem"
              @delete="onDeleteItem"
              @pin="onPinItem"
            />
          </div>
        </f7-block>
      </f7-page>
    </AdaptiveSheet>

    <SkeletonList v-if="loading" variant="card" :rows="3" class="feed-skeleton" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../common/AppLayout.vue";
import AdaptiveSheet from "../common/AdaptiveSheet.vue";
import SkeletonList from "../common/SkeletonList.vue";
import FeedFilter from "./FeedFilter.vue";
import FeedItemCard from "../feed/FeedItemCard.vue";
import FeedComposer from "../feed/FeedComposer.vue";
import ImportantEventCard from "../feed/ImportantEventCard.vue";
import ManageFeedDialogs from "../feed/ManageFeedDialogs.vue";
import { useFeedStore } from "../../js/pinia-stores/feed.js";
import { useImportantEvent } from "../../js/composables/useImportantEvent.js";
import { useKeycloakStore } from "../../js/pinia-stores/keycloak.js";
import { useAccountStore } from "../../js/pinia-stores/account.js";
import { useAuthStore } from "../../js/pinia-stores/auth.js";
import { useMessageStore } from "../../js/pinia-stores/message.js";
import {
  canManageItem as canManageItemUtil,
  isGroupAdmin,
} from "../../js/feedManageUtils.js";
import i18n from "../../js/i18n.js";

const props = defineProps({
  cards: { type: Array, default: () => [] },
  filters: { type: Array, default: () => [] },
  groupId: { type: String, default: null },
});

const { t } = i18n.global;

const feedStore = useFeedStore();
const keycloakStore = useKeycloakStore();
const accountStore = useAccountStore();
const authStore = useAuthStore();
const messageStore = useMessageStore();
const important = useImportantEvent(props.groupId);

const isLoggedIn = computed(() => keycloakStore.authenticated);
const userName = computed(
  () => authStore.profile?.name || accountStore.userAccount?.name || "",
);
const userAvatar = computed(() => accountStore.userAccount?.avatar || "");
const currentUserId = computed(
  () =>
    authStore.profile?.sub ||
    keycloakStore.tokenParsed?.sub ||
    accountStore.userAccount?.userId ||
    keycloakStore.subject ||
    "",
);
const loading = computed(() => feedStore.loading);
const chatGroupList = computed(() => messageStore.groupList || []);

const activeFilter = ref("all");
const visibleCount = ref(3);
const itemPopupOpened = ref(false);
const selectedItem = ref(null);

const searchQuery = computed(() => (feedStore.searchQuery || "").trim());
const searchActive = computed(() => searchQuery.value.length > 0);

function itemMatchesSearch(item) {
  const q = searchQuery.value.toLowerCase();
  if (!q) return true;
  const fields = [
    item.title,
    item.headline,
    item.text,
    item.description,
    item.authorName,
    item.userName,
    item.sponsorName,
    item.groupName,
    item.author?.name,
    item.poll?.title,
    item.campaignAttachment?.name,
    item.originalPost?.headline,
    item.originalPost?.text,
    item.originalPost?.authorName,
  ];
  return fields.some(
    (value) => value && String(value).toLowerCase().includes(q),
  );
}

function clearSearch() {
  feedStore.clearSearch();
}

const feedItems = computed(() => {
  let items = [];
  if (feedStore.items.length > 0) {
    items = feedStore.items;
    if (activeFilter.value !== "all") {
      items = items.filter((item) => {
        if (activeFilter.value === "posts") return item.type === "POST";
        if (activeFilter.value === "campaigns")
          return !!item.campaignAttachment || !!item.campaign;
        if (activeFilter.value === "polls")
          return item.type === "POLL" || item.type === "SPONSORED_POLL";
        if (activeFilter.value === "sponsored")
          return (
            item.type === "SPONSORED_POST" || item.type === "SPONSORED_POLL"
          );
        return true;
      });
    }
  } else if (props.cards.length > 0) {
    items = props.cards;
    if (activeFilter.value !== "all") {
      items = items.filter((c) => {
        if (activeFilter.value === "tontine")
          return (
            c.campaignType === "TONTINE" || c.campaignType === "GROUP_SAVINGS"
          );
        if (activeFilter.value === "savings")
          return (
            c.campaignType === "GROUP_SAVINGS" ||
            c.campaignType === "FUND_COLLECT"
          );
        if (activeFilter.value === "shop")
          return (
            c.campaignType === "GROUP_BUY" || c.campaignType === "PRIVATE_SHOP"
          );
        if (activeFilter.value === "events")
          return (
            c.campaignType === "TICKETING" ||
            c.campaignType === "EVENT_REGISTRATION" ||
            c.campaignType === "RSVP"
          );
        if (activeFilter.value === "posts")
          return c.kind === "campaign" || c.kind === "activity";
        if (activeFilter.value === "polls") return c.kind === "poll";
        if (activeFilter.value === "sponsored") return c.kind === "sponsored";
        return true;
      });
    }
  }
  if (searchActive.value) {
    items = items.filter(itemMatchesSearch);
  }
  return items;
});

onMounted(() => {
  loadFeedForContext();
  if (isLoggedIn.value) important.load();
  watch(
    () => props.groupId,
    () => loadFeedForContext(),
  );
  const revealAll = () => {
    visibleCount.value = feedItems.value.length;
  };
  if ("requestIdleCallback" in window) {
    requestIdleCallback(revealAll, { timeout: 2000 });
  } else {
    setTimeout(revealAll, 500);
  }
});

function loadFeedForContext() {
  if (!isLoggedIn.value) return;
  const targetGroup = props.groupId || null;
  if (feedStore.loadedGroupId !== targetGroup) {
    feedStore.reset();
    feedStore.loadedGroupId = targetGroup;
    if (targetGroup) {
      feedStore.loadGroup(targetGroup, currentUserId.value);
    } else {
      feedStore.loadMain(currentUserId.value);
    }
  } else if (feedStore.items.length === 0) {
    if (targetGroup) {
      feedStore.loadGroup(targetGroup, currentUserId.value);
    } else {
      feedStore.loadMain(currentUserId.value);
    }
  }
}

function onReact({ itemId, reactionType }) {
  feedStore.reactToItem({
    itemId,
    reactionType,
    userId: currentUserId.value,
    authorName: userName.value,
    authorAvatar: userAvatar.value,
  });
}

function onComment({ postId, text, attachments }) {
  feedStore.addComment({
    postId,
    comment: {
      id: `c-${Date.now()}`,
      text,
      attachments: stripAttachmentFiles(attachments),
      author: { name: userName.value, avatar: userAvatar.value },
      userId: currentUserId.value,
      timeAgo: t("common.timeAgo"),
      likesCount: 0,
      reactions: [],
      replies: [],
    },
  });
}

function onReply({ postId, commentId, text, attachments, replyTo }) {
  feedStore.addReply({
    postId,
    commentId,
    reply: {
      id: `r-${Date.now()}`,
      text,
      attachments: stripAttachmentFiles(attachments),
      replyTo,
      author: { name: userName.value, avatar: userAvatar.value },
      userId: currentUserId.value,
      timeAgo: t("common.timeAgo"),
      likesCount: 0,
      reactions: [],
    },
  });
}

function onReactComment({ postId, commentId, replyId, reactionType }) {
  feedStore.reactToComment({
    postId,
    commentId,
    replyId,
    reactionType,
    userId: currentUserId.value,
    authorName: userName.value,
    authorAvatar: userAvatar.value,
  });
}

function stripAttachmentFiles(attachments) {
  return (attachments || []).map(({ file, ...rest }) => rest);
}

function onRepost({ itemId, text }) {
  const target = feedStore.items.find((i) => i.id === itemId);
  feedStore.repostItem({
    postId: itemId,
    userId: currentUserId.value,
    authorName: userName.value,
    authorAvatar: userAvatar.value,
  });
  if (target) {
    feedStore.addRepostPost({
      item: target,
      text: text || "",
      userId: currentUserId.value,
      authorName: userName.value,
      authorAvatar: userAvatar.value,
    });
  }
  f7.toast.show({
    text: t("feed.repostSuccess"),
    position: "center",
    closeTimeout: 1200,
  });
}

function onVote({ pollId, optionId }) {
  feedStore.votePoll({ pollId, optionId, userId: currentUserId.value });
}

const manageDialogs = ref(null);

function canManageItem(item) {
  const accountGroups = accountStore.userGroupList || [];
  const messageGroups = chatGroupList.value || [];
  return canManageItemUtil(item, currentUserId.value, (it) =>
    isGroupAdmin(it.groupId, currentUserId.value, accountGroups, messageGroups),
  );
}

function openItemPopup(item) {
  selectedItem.value = item;
  itemPopupOpened.value = true;
}

function onEditItem(item) {
  manageDialogs.value?.openEdit(item);
}

function onDeleteItem(item) {
  manageDialogs.value?.confirmDelete(item);
}

function goManage() {
  f7.views.main.router.navigate("/feed/manage");
}

function openImportantEvent(event) {
  const targetGroup = important.groupId.value;
  if (!targetGroup) return;
  const startAt = event?.startAt || "";
  f7.views.main.router.navigate(
    `/groups/${targetGroup}/calendar/?eventId=${
      event.id
    }&start=${encodeURIComponent(startAt)}`,
  );
}

async function onPinItem({ item, pinned }) {
  await feedStore.pinItem({ item, pinned });
  f7.toast.show({
    text: pinned ? t("feed.pinnedToTop") : t("feed.unpinnedFromTop"),
    position: "center",
    closeTimeout: 1200,
  });
}

function onCreatePost(postData) {
  feedStore.createPost({
    ...postData,
    groupId: props.groupId || undefined,
    authorName: userName.value,
    authorAvatar: userAvatar.value,
    author: {
      userId: currentUserId.value,
      name: userName.value,
      avatar: userAvatar.value,
    },
    type: "POST",
    timeAgo: t("common.timeAgo"),
    reactions: [],
    comments: [],
    commentsCount: 0,
  });
}

function onCreatePoll(pollData) {
  const isFullPoll =
    Array.isArray(pollData.questions) && pollData.questions.length > 0;
  feedStore.createPost({
    headline: pollData.title || "",
    text: pollData.description || "",
    type: "POLL",
    groupId: props.groupId || undefined,
    authorName: userName.value,
    authorAvatar: userAvatar.value,
    author: {
      userId: currentUserId.value,
      name: userName.value,
      avatar: userAvatar.value,
    },
    poll: isFullPoll
      ? {
          id: pollData.id || `poll-${Date.now()}`,
          ...pollData,
          totalVotes: 0,
          userVote: null,
        }
      : {
          id: `poll-${Date.now()}`,
          title: pollData.title,
          options: pollData.options || [],
          totalVotes: 0,
          userVote: null,
        },
    timeAgo: t("common.timeAgo"),
    reactions: [],
    comments: [],
    commentsCount: 0,
  });
}

function onShareToChat({ group, feedItem }) {
  const chatUser = messageStore.chatUser;
  if (!chatUser || !chatUser.userId) {
    f7.toast.show({
      text: t("common.joinAvocashFisrt"),
      position: "center",
      closeTimeout: 2000,
    });
    return;
  }
  const feedCardData = {
    id: feedItem.id,
    type: feedItem.type,
    authorName: feedItem.authorName,
    text: feedItem.text || "",
    headline: feedItem.headline || "",
    media: (feedItem.media || []).slice(0, 2),
    groupName: feedItem.groupName || "",
    poll: feedItem.poll
      ? { options: (feedItem.poll.options || []).slice(0, 4) }
      : null,
  };
  f7.views.main.router.navigate(`/message/${group.id}/${group.groupType}`, {
    reloadAll: false,
    reloadDetail: true,
  });
  f7.toast.show({
    text: t("common.sharedInGroup", { groupName: group.name }),
    position: "center",
    closeTimeout: 1500,
  });
}
</script>

<style scoped>
.home-feed {
  margin-bottom: 10px;
}
.feed-important {
  margin-bottom: 10px;
}
.feed-empty {
  text-align: center;
  padding: 30px 0;
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.feed-empty p {
  margin-top: 8px;
}
.feed-manage-bar {
  display: flex;
  justify-content: flex-end;
  padding: 0 0 6px;
}
.feed-manage-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--avocash-green, #1a6b42);
}
.feed-search-active {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.03);
}
.feed-search-query {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--f7-theme-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.feed-search-clear {
  flex-shrink: 0;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.feed-item-popup {
  /* --f7-popup-tablet-width: 620px; */
  /* --f7-popup-tablet-height: 90vh; */
}
.feed-item-popup-inner {
  /* height: 100%; */
  /* overflow-y: auto; */
  /* background: var(--light); */
}
</style>
